import { useEffect, useRef, useState } from 'react';
import type { ResearchJob } from '../stores/researchJobsStore';

/**
 * Progress presentation state for a long-running case-wizard research job.
 *
 * (Distinct from `useJobProgress`, which polls SEC-report processing jobs via
 * a different endpoint and owns its own fetching. This hook does no fetching —
 * `ResearchJobsNotifier` already polls these jobs into `researchJobsStore`; it
 * derives presentation state from what is already there.)
 *
 * The backend's progress checkpoints are unevenly spaced — for industry
 * research they run 3 / 5 / 15 / 30 / 85 / 100, and everything between 30 and
 * 85 is a single LLM call that cannot report from inside itself. So the bar
 * legitimately sits at 30% for most of the wall-clock time, which reads as
 * "frozen" rather than "working".
 *
 * This hook does not invent progress. It detects that the reported number has
 * stopped moving and lets the UI switch to an indeterminate presentation,
 * which states "working, duration unknown" honestly instead of implying
 * measurable advancement that isn't happening.
 */

/** How long progress must sit unchanged before we call it stalled. */
export const STALL_MS = 8000;

/** How long a job may run before we stop waiting and offer a retry. */
export const JOB_TIMEOUT_MS = 5 * 60 * 1000;

/** Above this many seconds, "typical run time: 30-60s" is actively misleading. */
export const TYPICAL_RUN_SECONDS = 60;

/** Ticker cadence for elapsed time and the stall/timeout thresholds. */
const TICK_MS = 1000;

export interface ResearchJobProgressState {
  /** Seconds since the job was registered. */
  elapsedSeconds: number;
  /** True once progress has not changed for STALL_MS. */
  isStalled: boolean;
  /** True once the job has run past JOB_TIMEOUT_MS without finishing. */
  isTimedOut: boolean;
  /** True once elapsed exceeds the advertised typical run time. */
  isSlowerThanTypical: boolean;
}

/**
 * Track elapsed time, stall and timeout for a running research job.
 *
 * Pass `null` when no job is active — the hook resets and stops ticking.
 */
export function useResearchJobProgress(
  job: Pick<ResearchJob, 'progress' | 'status' | 'createdAt'> | null | undefined,
): ResearchJobProgressState {
  const progress = job?.progress;
  const status = job?.status;
  const createdAt = job?.createdAt;
  const isRunning = !!job && status === 'running';

  // `now` drives elapsed/stall/timeout. Derived values must not be computed
  // inline from Date.now() during render — that reads a value React never
  // re-renders on, so the UI would freeze at whatever the first render saw.
  const [now, setNow] = useState(() => Date.now());
  const [stalledSince, setStalledSince] = useState<number | null>(null);
  const lastProgress = useRef<number>(-1);

  // Reset the stall clock whenever the reported progress actually moves.
  useEffect(() => {
    if (progress !== lastProgress.current) {
      lastProgress.current = progress ?? -1;
      setStalledSince(Date.now());
    }
  }, [progress]);

  // Tick only while a job is running, so a finished job stops the timer.
  useEffect(() => {
    if (!isRunning) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [isRunning]);

  // Clear the stall clock when the job stops running, so a subsequent job
  // doesn't inherit the previous one's stall state.
  useEffect(() => {
    if (!isRunning) setStalledSince(null);
  }, [isRunning]);

  const elapsedMs = createdAt ? Math.max(0, now - createdAt) : 0;

  return {
    elapsedSeconds: Math.floor(elapsedMs / 1000),
    isStalled: isRunning && stalledSince !== null && now - stalledSince > STALL_MS,
    isTimedOut: isRunning && !!createdAt && elapsedMs > JOB_TIMEOUT_MS,
    isSlowerThanTypical: elapsedMs / 1000 > TYPICAL_RUN_SECONDS,
  };
}

/** Format elapsed seconds as "45s" or "2m 05s". */
export function formatElapsed(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}
